/*
  This file is for handling the conditional render of menu
*/
import dayjs from 'dayjs';
import dayjsIsSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import lodashFilter from 'lodash/filter';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsUndefined from 'lodash/isUndefined';

import StoreHelper from './Store';

dayjs.extend(dayjsIsSameOrAfter);

// this is the array needed when lodash picking on item object when saving/updating on redux aside from api data
const additionalDataNeededForCart = [
  'active_discount',
  'discount_type',
  'discount_value',
  'is_bulk',
];

const getStock = (itemStock, categoryStock) => {
  const hasCategoryLimit = categoryStock !== -1;
  const hasItemLimit = itemStock !== -1;
  if (hasItemLimit) {
    // if has item limit
    if (hasCategoryLimit) {
      // if has category limit
      if (itemStock >= categoryStock) {
        // if item stock is more than category stock
        return {
          stock: categoryStock,
          isCategoryStock: true,
          hasCategoryLimit,
          hasItemLimit,
        }; // use category stock
      }
    }
  } else if (hasCategoryLimit) {
    // if no item limit but has category limit
    return {
      stock: categoryStock,
      isCategoryStock: true,
      hasCategoryLimit,
      hasItemLimit,
    }; // use category stock
  }
  // if condition above not met
  return {
    stock: itemStock,
    isCategoryStock: false,
    hasCategoryLimit,
    hasItemLimit,
  }; // use item stock
};

// general function for displaying category or item stock purpose
const getCategoryOrItemStock = ({
  item_limit_per_day,
  item_limit_per_time_slot,
  item,
  stock,
  category_stock,
}) => {
  const actualStock = item?.stock || stock;

  const geStock = () => {
    const isCategoryStockLimitLow = category_stock >= 0 && category_stock <= 10;

    if (isCategoryStockLimitLow && category_stock < actualStock) {
      return category_stock;
    }
    return actualStock >= 0 && actualStock <= 10 ? actualStock : undefined; // only show stock if low (0 - 10)
  };

  if (
    typeof item_limit_per_day !== 'undefined' ||
    typeof item_limit_per_time_slot !== 'undefined'
  ) {
    // item item_limit_per_day & item_limit_per_time_slot?.[0]?.limit no limit value is 0, unlike stock no limit is -1
    const limit = item_limit_per_day || item_limit_per_time_slot?.[0]?.limit; // index 0 bcs all index is same value
    if (typeof limit === 'number' && limit !== 0) {
      // if item has limit, only show stock
      return geStock();
    } else {
      // if no limit, just return undefined
      return undefined;
    }
  } else {
    // category or not item
    return geStock();
  }
};

/**
 * Get the available stock for an item/category considering the quantity in the cart.
 * @param {object} item - The item object containing its details and stock, including category stock or just { "id/item_id": "", stock: 1, category_name: "", category_stock: 1 }
 * @param {object} shopData - The shop data containing information about items, categories, and their menu or just { id: "", menu_list: [] }
 * @param {object} cartData - The cart data of the shop.
 * @returns {object} - An object containing stock information.
 *   - categoryName (string): The name of the category to which the item belongs.
 *   - isCategoryStock (boolean): Whether the category has its own stock limit independent of individual items.
 *   - remainingStock (number): The remaining stock of the item after checking the cart and item/category limit.
 *   - stock (number): The available stock for the requested item, considering items already in the cart.
 */
const getItemStockConsideringCart = (item, shopData, cartData) => {
  const categoryStock = lodashIsUndefined(item?.category_stock)
    ? -1
    : item.category_stock;
  const itemStock = lodashIsUndefined(item?.stock) ? -1 : item.stock;
  const { hasCategoryLimit, hasItemLimit, isCategoryStock, stock } = getStock(
    itemStock,
    categoryStock,
  );
  const itemId = item.id || item.item_id;
  let remainingStock = stock;
  let isCategory = isCategoryStock; // just re initialize it, will only overwrite it if item stock is not enough for category stock
  let categoryName = '';
  if (!lodashIsEmpty(shopData)) {
    // get store carts of the store
    const storeCarts = StoreHelper.getStoreCartData(shopData.id, cartData);
    if (storeCarts?.items?.length >= 0) {
      // if has cart, check existing quantity on the cart
      let sameCategoryQty = 0;
      let sameItemQty = 0;
      storeCarts.items.forEach((storeCart) => {
        // get this item category on cart item
        const cartItemInCategory = lodashFind(
          shopData.menu_list,
          (menu) => menu.item?.id === storeCart.item_id,
        );
        if (cartItemInCategory?.category === item?.category_name) {
          // if from same category
          sameCategoryQty += storeCart.quantity;
          if (storeCart.item_id === itemId) {
            // if same item
            sameItemQty += storeCart.quantity;
          }
        }
      });
      // process the sameCategoryQty & sameItemQty
      if (isCategoryStock) {
        // if hitting the category limit and same category item in cart is less than or equal the stock
        remainingStock = stock - sameCategoryQty; // reduce the same category item to the stock
      } else if (hasCategoryLimit && hasItemLimit) {
        // if has category limit and item limit and same item in cart is less than or equal the stock
        // e.g: categoryStock = 5, itemStock = 3, sameCategoryQty = 3, sameItemQty = 2
        const existingCategoryQty = sameCategoryQty - sameItemQty; // 3 - 2 = 1 (exclude same item on same category)
        const totalCategoryQty = existingCategoryQty + sameItemQty; // 1 + 2 = 3 (count all same category)
        const remainingCategoryStock = categoryStock - totalCategoryQty; // 5 - 3 = 2(check remaining stock)
        const useItemStock = remainingCategoryStock + sameItemQty >= itemStock; // 2 + 2 = 4, 4 >= 3 (check if going to use the item stock instead of category stock)
        if (useItemStock) {
          remainingStock = itemStock - sameItemQty; // 3 - 2 = 1 (store remainingStock)
        } else {
          isCategory = true; // flag limit as category limit
          remainingStock = categoryStock - sameCategoryQty; // 5 - 3 = 2 (store remainingStock)
        }
      } else if (hasItemLimit) {
        // if has item limit / hitting item limit and same item in cart is less than or equal the stock
        remainingStock = stock - sameItemQty; // reduce the same item on the cart to stock
      }
      // handle if remainingStock become < 0 while it has category and item stock limit
      if ((hasCategoryLimit || hasItemLimit) && remainingStock < 0) {
        remainingStock = 0; // set remainingStock as 0 bcs cart qty is more than the stock might be due to other user already bought it
      }
    }
    categoryName = item?.category_name;
  }
  // return the information
  return {
    categoryName,
    isCategoryStock: isCategory,
    remainingStock,
    stock,
  };
};

const hasDiscount = (item) => {
  if (lodashIsEmpty(item)) {
    return false;
  } else if (item.active_discount <= 0) {
    return false;
  } else if (!item.discount_type || !item.discount_value) {
    return false;
  } else if (item.discount_type === 'noDiscount') {
    return false;
  }
  return true;
};

// only use on store page for not including the item on store menu, getAvailableItem return boolean
const getAvailableItem = (categoryItem, isMealPlan, order_date) => {
  const hasStock = categoryItem.stock === -1 || categoryItem.stock > 0;
  const isOffDate = categoryItem.item.off_dates?.includes(order_date); //TODO:
  const isAvailable = categoryItem.available && !isOffDate;
  // visible only if visible flag is true or offDate and visible on off date flag is true
  const isVisible =
    categoryItem.visible ||
    (categoryItem.item.is_visible_on_off_date && isOffDate);
  // if has stock, check if available or visible, if no stock just check if visible
  const shouldShow = hasStock ? isAvailable || isVisible : isVisible;

  if (isMealPlan) {
    // menu item should show if shouldShow is true and meal plan
    return shouldShow && categoryItem.item.is_meal_plan;
  } else {
    // menu item should show if shouldShow is true and not meal plan
    return shouldShow && !categoryItem.item.is_meal_plan;
  }
};

const showDiscountDescription = (item) => {
  return item?.active_discount > 0 && !!item?.discount_description;
};

/**
 * Validates whether the selected extras fulfill all required extra groups.
 * @param {import('../RTK/api/types').ExtraGroup} groupItem - Array of extra group objects that may include requirements like min/max selections.
 * @returns {{ isMultiple: boolean; isRequired: boolean; note: string }}
 * An object containing:
 * - `isMultiple`: if the extra group is multiple selection,
 * - `isRequired`: if the extra group is required,
 * - `note`: note to display usually above the selection
 */
const getExtraGroupSelectionInfo = (extraGroup) => {
  const isSelectAll = extraGroup.minimum_number === extraGroup.maximum_number;
  const requiredNote = `Select ${extraGroup.maximum_number}`;
  const multiNote = extraGroup.is_required
    ? `Select ${extraGroup.minimum_number} up to ${extraGroup.maximum_number}`
    : `Select up to ${extraGroup.maximum_number}`;
  return {
    isMultiple: extraGroup.minimum_number > 1 || !isSelectAll,
    isRequired: extraGroup.is_required,
    note: isSelectAll ? requiredNote : multiNote,
  };
};

/**
 * Validates whether the selected extras fulfill all required extra groups.
 * @param {string[]} selectedExtras - selected extra item IDs.
 * @param {import('../RTK/api/types').ExtraGroup[]} extraGroup - item overall extra group selection
 * @returns {{ ok: boolean, count: string, groupName: string }}
 * An object containing:
 * - `ok`: whether all required groups are fulfilled,
 * - `count`: expected string of selections or range,
 * - `groupName`: the name of the group that is not yet fulfilled.
 */
const validateSelectedExtras = (selectedExtras, extraGroup) => {
  for (let eGroup of lodashFilter(extraGroup, { is_required: true })) {
    const extraGroupIds = eGroup.data.map((ee) => ee.id);
    const selectedIdsOnExtraGroup = selectedExtras.filter((e) =>
      extraGroupIds.includes(e),
    );
    const isMultiple = eGroup.maximum_number > 1;
    const isRange = eGroup.minimum_number !== eGroup.maximum_number;
    let isInvalid = selectedIdsOnExtraGroup.length === 0;
    let requiredNote = '1';
    if (isMultiple) {
      const val = selectedIdsOnExtraGroup.length;
      const min = eGroup.minimum_number;
      const max = eGroup.maximum_number;

      if (isRange) {
        isInvalid = !(val >= min && val <= max);
        requiredNote = `${min} - ${max}`;
      } else {
        isInvalid = val !== max;
        requiredNote = `${max}`;
      }
    }
    if (isInvalid) {
      return { ok: false, count: requiredNote, groupName: eGroup.name };
    }
  }
  // return as valid since the loop above success processing
  return { ok: true, count: '', groupName: '' };
};

export default {
  additionalDataNeededForCart,
  getCategoryOrItemStock,
  getItemStockConsideringCart,
  hasDiscount,
  getAvailableItem,
  showDiscountDescription,
  getExtraGroupSelectionInfo,
  validateSelectedExtras,
};
